import {
  type ProviderInstanceId,
  type ProviderDriverKind,
  type ResolvedKeybindingsConfig,
} from "@t3tools/contracts";
import { resolveSelectableModel } from "@t3tools/shared/model";
import { LegendList, type LegendListRef } from "@legendapp/list/react";
import { memo, useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { ChevronRightIcon, SearchIcon } from "lucide-react";
import { ModelListRow, type ModelRowPresentation } from "./ModelListRow";
import {
  ModelPickerSidebar,
  type ModelPickerRailCompany,
  type ModelPickerRailSelection,
} from "./ModelPickerSidebar";
import {
  CHAT_GROUPS,
  PREVIEW_COMPANIES,
  PREVIEW_COMPANY_BY_ID,
  RAIL_PROVIDERS,
  UTILITY_GROUPS,
  type PreviewPlatform,
  isComingSoonProviderId,
  isPreviewCompanyId,
  isPreviewInstanceId,
} from "./morrowModelCatalog";
import {
  modelPickerLegacySectionKey,
  modelPickerModelKey,
  parseModelPickerLegacySectionKey,
  parseModelPickerModelKey,
} from "./modelPickerKeys";
import { isModelPickerNewModel } from "./modelPickerModelHighlights";
import { buildModelPickerSearchText, scoreModelPickerSearch } from "./modelPickerSearch";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxListVirtualized,
} from "../ui/combobox";
import { type Icon, ModelEsque } from "./providerIconUtils";
import {
  modelPickerJumpCommandForIndex,
  modelPickerJumpIndexFromCommand,
  resolveShortcutCommand,
  shortcutLabelForCommand,
} from "../../keybindings";
import { useClientSettings, useUpdateClientSettings } from "~/hooks/useSettings";
import { cn } from "~/lib/utils";
import { getVirtualizedScrollFadeClassName } from "../ui/scroll-area";
import { TooltipProvider } from "../ui/tooltip";
import {
  isMorrowHostedDriver,
  isMorrowHostedModel,
  isProviderInstancePickerReady,
  type ProviderInstanceEntry,
} from "../../providerInstances";
import { providerModelKey, sortProviderModelItems } from "../../modelOrdering";
import { useMorrowPlan, type MorrowTier } from "../../morrowPlan";
import { openUpgradeModal } from "../../upgradeModal";

// A model tier (free/pay/super on the catalog) maps to the boolean capability
// the entitlement engine gates it behind. Free has no gate.
const MODEL_TIER_CAPABILITY: Partial<Record<MorrowTier, string>> = {
  pay: "model.tier.pay",
  super: "model.tier.super",
  max: "model.tier.max",
};

type ModelPickerItem = {
  slug: string;
  name: string;
  shortName?: string;
  subProvider?: string;
  instanceId: ProviderInstanceId;
  driverKind: ProviderDriverKind;
  instanceDisplayName: string;
  instanceAccentColor?: string | undefined;
  continuationGroupKey?: string | undefined;
  isLegacy?: boolean | undefined;
};

const EMPTY_MODEL_JUMP_LABELS = new Map<string, string>();

const SECTION_HEADER_KEY_PREFIX = "section-header:";

function sectionHeaderKey(id: string): string {
  return `${SECTION_HEADER_KEY_PREFIX}${id}`;
}

function parseSectionHeaderKey(key: string): string | null {
  return key.startsWith(SECTION_HEADER_KEY_PREFIX)
    ? key.slice(SECTION_HEADER_KEY_PREFIX.length)
    : null;
}

/**
 * Flattened preview (locked showcase) rows, derived once from the static
 * catalog. Each carries a `presentation` blob for the richer row, the provider
 * icon glyph, and enough metadata to render — but they are DISPLAY ONLY and are
 * never resolvable through the real selection pipeline (their instance ids are
 * prefixed and absent from `modelOptionsByInstance`).
 */
type PreviewRowItem = {
  key: string;
  instanceId: ProviderInstanceId;
  driverKind: ProviderDriverKind;
  displayName: string;
  icon: Icon;
  isUtility: boolean;
  model: ModelEsque;
  presentation: ModelRowPresentation;
};

type PreviewGroupItem = {
  headerKey: string;
  headerId: string;
  displayName: string;
  driverKind: ProviderDriverKind;
  icon: Icon;
  platform: PreviewPlatform;
  isUtility: boolean;
  rows: PreviewRowItem[];
};

function toPreviewGroup(group: (typeof CHAT_GROUPS)[number]): PreviewGroupItem {
  const isUtility = group.kind === "utility";
  return {
    headerId: group.instanceId,
    headerKey: sectionHeaderKey(group.instanceId),
    displayName: group.displayName,
    driverKind: group.driverKind,
    icon: group.icon,
    platform: group.platform,
    isUtility,
    rows: group.models.map((m) => ({
      key: modelPickerModelKey(group.instanceId, m.slug),
      instanceId: group.instanceId,
      driverKind: group.driverKind,
      displayName: group.displayName,
      icon: group.icon,
      isUtility,
      model: {
        slug: m.slug,
        name: m.name,
        ...(m.shortName ? { shortName: m.shortName } : {}),
      },
      presentation: {
        description: m.description,
        capabilities: m.capabilities,
        ...(m.context ? { context: m.context } : {}),
        ...(m.tier ? { tier: m.tier } : {}),
        ...(isUtility ? { isBackground: true } : {}),
      },
    })),
  };
}

const PREVIEW_GROUPS: ReadonlyArray<PreviewGroupItem> = CHAT_GROUPS.map(toPreviewGroup);
const UTILITY_PREVIEW_GROUPS: ReadonlyArray<PreviewGroupItem> = UTILITY_GROUPS.map(toPreviewGroup);

// Toggle row that expands/collapses the whole utility (background) section.
const UTILITY_TOGGLE_KEY = "utility-toggle";

const PREVIEW_ROW_BY_KEY: ReadonlyMap<string, PreviewRowItem> = new Map(
  [...PREVIEW_GROUPS, ...UTILITY_PREVIEW_GROUPS].flatMap((group) =>
    group.rows.map((row) => [row.key, row] as const),
  ),
);

const PREVIEW_GROUP_BY_HEADER_ID: ReadonlyMap<string, PreviewGroupItem> = new Map(
  [...PREVIEW_GROUPS, ...UTILITY_PREVIEW_GROUPS].map((group) => [group.headerId, group] as const),
);

const UTILITY_MODEL_COUNT = UTILITY_PREVIEW_GROUPS.reduce(
  (total, group) => total + group.rows.length,
  0,
);

function ModelListSeparator() {
  return <div className="h-0.5" />;
}

const PLATFORM_LABEL: Record<PreviewPlatform, string> = {
  cloudflare: "Workers AI",
  bedrock: "Bedrock",
  vertex: "Vertex",
};

function ProviderSectionHeader({
  displayName,
  icon: Icon,
  platform,
}: {
  displayName: string;
  icon: Icon | null;
  platform?: PreviewPlatform;
}) {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-1.5 bg-muted/95 px-1 py-1 backdrop-blur-sm">
      {Icon ? <Icon className="size-3.5 shrink-0" /> : null}
      <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        {displayName}
      </span>
      {platform ? (
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/60">
          · {PLATFORM_LABEL[platform]}
        </span>
      ) : null}
      <span className="ml-1 h-0.5 flex-1 bg-border" />
    </div>
  );
}

export const ModelPickerContent = memo(function ModelPickerContent(props: {
  /** The instance currently selected in the composer (combobox "value"). */
  activeInstanceId: ProviderInstanceId;
  model: string;
  /**
   * When set, the picker is locked to the given driver kind — typically
   * because the user is editing a previously-sent message and can't change
   * which driver served the turn. Multiple instances of the same kind
   * remain selectable (e.g. locked to `codex` still lets the user switch
   * between the default Codex and a custom Codex Personal).
   */
  lockedProvider: ProviderDriverKind | null;
  lockedContinuationGroupKey?: string | null;
  /**
   * All configured provider instances in display order. Used to render
   * the sidebar (one button per instance) and to resolve display names
   * for the locked-mode header.
   */
  instanceEntries: ReadonlyArray<ProviderInstanceEntry>;
  keybindings?: ResolvedKeybindingsConfig;
  /**
   * Model options per instance. Keyed by `ProviderInstanceId` so the
   * default Codex instance and any custom Codex instances each have their
   * own list (custom instances typically start with the same built-in
   * model set but are free to diverge via customModels).
   */
  modelOptionsByInstance: ReadonlyMap<ProviderInstanceId, ReadonlyArray<ModelEsque>>;
  terminalOpen: boolean;
  onRequestClose?: () => void;
  getModelDisabledReason?: (instanceId: ProviderInstanceId, model: string) => string | null;
  onInstanceModelChange: (instanceId: ProviderInstanceId, model: string) => void;
}) {
  const {
    keybindings: providedKeybindings,
    modelOptionsByInstance,
    instanceEntries,
    getModelDisabledReason,
    onInstanceModelChange,
  } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [showTopScrollFade, setShowTopScrollFade] = useState(false);
  const [showBottomScrollFade, setShowBottomScrollFade] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const modelListRef = useRef<LegendListRef | null>(null);
  const highlightedModelKeyRef = useRef<string | null>(null);
  const favorites = useClientSettings((s) => s.favorites ?? []);
  // The rail selection is either the Favorites pseudo-entry or a provider
  // COMPANY id (e.g. `morrow-company-meta`). A company selection filters the
  // main list to that company's catalog group(s). Real configured models stay
  // visible at the top of the list regardless of the company chosen.
  const [selectedRailId, setSelectedRailId] = useState<ModelPickerRailSelection>(
    () =>
      // MorrowOS: open on the FIRST provider (OpenAI, per fame order) — not Favorites.
      // (When locked to a prior turn's driver the rail is hidden anyway; the real
      // configured instance's models still surface at the top of the list.)
      PREVIEW_COMPANIES[0]?.id ?? "favorites",
  );
  const selectedCompany = isPreviewCompanyId(selectedRailId)
    ? (PREVIEW_COMPANY_BY_ID.get(selectedRailId) ?? null)
    : null;
  const isFavoritesSelected = selectedRailId === "favorites";
  const [expandedLegacyInstances, setExpandedLegacyInstances] = useState(
    () =>
      new Set<ProviderInstanceId>(
        modelOptionsByInstance
          .get(props.activeInstanceId)
          ?.some((model) => model.slug === props.model && model.isLegacy)
          ? [props.activeInstanceId]
          : [],
      ),
  );
  // The utility (background) preview section is collapsed by default.
  const [utilitySectionExpanded, setUtilitySectionExpanded] = useState(false);
  const keybindings = useMemo<ResolvedKeybindingsConfig>(
    () => providedKeybindings ?? [],
    [providedKeybindings],
  );
  const updateSettings = useUpdateClientSettings();
  const plan = useMorrowPlan();

  // Freemium upgrade / add-key affordance. Rows locked purely for lack of a real
  // provider (no billing tier involved) fall back to this soft action: dismiss
  // the picker. Tier-locked rows use the capability-aware handler below.
  const handleUpgrade = useCallback(() => {
    props.onRequestClose?.();
  }, [props]);

  // A catalog model's tier exceeds the subject's entitlement → gate it. Opens
  // the global upgrade modal (capability + required tier) and closes the picker.
  const handleTierUpgrade = useCallback(
    (tier: MorrowTier) => {
      const capability = MODEL_TIER_CAPABILITY[tier];
      openUpgradeModal({
        requiredTier: tier,
        ...(capability ? { capability } : {}),
      });
      props.onRequestClose?.();
    },
    [props],
  );

  const focusSearchInput = useCallback(() => {
    searchInputRef.current?.focus({ preventScroll: true });
  }, []);

  const handleSelectRail = useCallback(
    (id: ModelPickerRailSelection) => {
      // Coming-soon providers are display-only: selecting one is a no-op and
      // must never change the model list. (The sidebar already blocks the
      // click; this is defense in depth.)
      if (isComingSoonProviderId(id)) {
        return;
      }
      setSelectedRailId(id);
      window.requestAnimationFrame(() => {
        focusSearchInput();
      });
    },
    [focusSearchInput],
  );

  useLayoutEffect(() => {
    focusSearchInput();
    const frame = window.requestAnimationFrame(() => {
      focusSearchInput();
    });
    const timeout = window.setTimeout(() => {
      focusSearchInput();
    }, 0);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [focusSearchInput]);

  // Create a Set for efficient lookup. Favorites are keyed by
  // `${instanceId}:${slug}`; the storage schema widened from ProviderDriverKind
  // to ProviderInstanceId so pre-migration favorites keyed by driver slugs
  // (e.g. `"codex:gpt-5"`) still resolve — the default instance id equals
  // the driver slug.
  const favoritesSet = useMemo(() => {
    return new Set(favorites.map((fav) => providerModelKey(fav.provider, fav.model)));
  }, [favorites]);

  /**
   * Lookup table keyed by `instanceId`. Used for display name + driver
   * kind enrichment and for `ready`/enabled filtering before flattening
   * models into the search list.
   */
  const entryByInstanceId = useMemo(
    () => new Map(instanceEntries.map((entry) => [entry.instanceId, entry])),
    [instanceEntries],
  );
  const matchesLockedProvider = useCallback(
    (entry: Pick<ProviderInstanceEntry, "driverKind" | "continuationGroupKey">): boolean => {
      if (props.lockedProvider === null) return true;
      if (entry.driverKind !== props.lockedProvider) return false;
      if (!props.lockedContinuationGroupKey) return true;
      // A model/instance with no continuation scoping (e.g. the native
      // morrow-agent) can't belong to the "wrong" continuation, so keep it.
      // Without this guard a stale or cross-provider locked continuation key
      // (a codex session key leaking onto a morrow-agent thread) filters out an
      // unscoped provider's ENTIRE catalog → "No models found".
      if (entry.continuationGroupKey == null) return true;
      return entry.continuationGroupKey === props.lockedContinuationGroupKey;
    },
    [props.lockedContinuationGroupKey, props.lockedProvider],
  );

  const readyInstanceSet = useMemo(() => {
    const ready = new Set<ProviderInstanceId>();
    for (const entry of instanceEntries) {
      if (isProviderInstancePickerReady(entry)) {
        ready.add(entry.instanceId);
      }
    }
    return ready;
  }, [instanceEntries]);

  // Flatten models into a searchable array. One pass over the
  // instance-keyed map; each model carries its instance id + driver kind
  // so the list row can render the right icon and display name without
  // another lookup.
  const flatModels = useMemo(() => {
    const out: ModelPickerItem[] = [];
    for (const [instanceId, models] of modelOptionsByInstance) {
      const entry = entryByInstanceId.get(instanceId);
      if (!entry) {
        // Instance disappeared between renders (configuration change). Skip
        // its models — stale options shouldn't appear in the picker.
        continue;
      }
      if (!readyInstanceSet.has(instanceId)) {
        continue;
      }
      for (const model of models) {
        out.push({
          slug: model.slug,
          name: model.name,
          ...(model.shortName ? { shortName: model.shortName } : {}),
          ...(model.subProvider ? { subProvider: model.subProvider } : {}),
          ...(model.isLegacy ? { isLegacy: true } : {}),
          instanceId,
          driverKind: entry.driverKind,
          instanceDisplayName: entry.displayName,
          ...(entry.accentColor ? { instanceAccentColor: entry.accentColor } : {}),
          ...(entry.continuationGroupKey
            ? { continuationGroupKey: entry.continuationGroupKey }
            : {}),
        });
      }
    }
    return out;
  }, [modelOptionsByInstance, entryByInstanceId, readyInstanceSet]);

  const isLocked = props.lockedProvider !== null;
  // The native MorrowOS agent is effectively the only provider, so a thread
  // "locked" to it must NOT collapse the picker into the bare flat list the way
  // a locked coding-agent thread does — the user should still get the full
  // company-grouped catalog they see when unlocked (signed out / on a draft).
  // Any MorrowOS-hosted selection counts as the native lock — the driver kind
  // isn't always the literal "morrow-agent" (hosted models also live under
  // @cf/… and bare Bedrock ids), so match the selected MODEL too. Without this,
  // a hosted-but-non-"morrow-agent" locked thread collapses the catalog to
  // "No models found" on reopen.
  const isNativeAgentLock =
    isMorrowHostedDriver(props.lockedProvider) || isMorrowHostedModel(props.model);
  const catalogChromeHidden = isLocked && !isNativeAgentLock;
  const isSearching = searchQuery.trim().length > 0;
  // The rail now lists OUR provider COMPANIES (one brand icon each, deduped
  // across Cloudflare + Bedrock) rather than the old coding-agent driver
  // instances. Companies come straight from the catalog.
  // Active companies (click-to-filter) followed by the coming-soon roster
  // (dimmed, non-clickable). RAIL_PROVIDERS already orders them that way.
  const railCompanies = useMemo<ReadonlyArray<ModelPickerRailCompany>>(
    () =>
      RAIL_PROVIDERS.map((provider) => ({
        id: provider.id,
        displayName: provider.displayName,
        icon: provider.icon,
        simpleIcon: provider.simpleIcon,
        comingSoon: provider.comingSoon,
      })),
    [],
  );
  // Sidebar is hidden while searching or when locked to a prior turn's driver
  // (company filtering doesn't apply there — the real instance's models show
  // as a flat searchable/locked list instead).
  const showSidebar = !isSearching && !catalogChromeHidden && railCompanies.length > 0;
  const instanceOrder = useMemo(
    () => instanceEntries.map((entry) => entry.instanceId),
    [instanceEntries],
  );

  // Filter models based on search query and selected instance
  const filteredModels = useMemo(() => {
    let result = flatModels;

    // Apply tokenized fuzzy search across the combined provider/model search fields.
    if (searchQuery.trim()) {
      const rankedMatches = result
        .map((model) => ({
          model,
          score: scoreModelPickerSearch(
            {
              name: model.name,
              ...(model.shortName ? { shortName: model.shortName } : {}),
              ...(model.subProvider ? { subProvider: model.subProvider } : {}),
              driverKind: model.driverKind,
              providerDisplayName: model.instanceDisplayName,
              isFavorite: favoritesSet.has(providerModelKey(model.instanceId, model.slug)),
            },
            searchQuery,
          ),
          isFavorite: favoritesSet.has(providerModelKey(model.instanceId, model.slug)),
          tieBreaker: buildModelPickerSearchText({
            name: model.name,
            ...(model.shortName ? { shortName: model.shortName } : {}),
            ...(model.subProvider ? { subProvider: model.subProvider } : {}),
            driverKind: model.driverKind,
            providerDisplayName: model.instanceDisplayName,
          }),
        }))
        .filter(
          (
            rankedModel,
          ): rankedModel is {
            model: ModelPickerItem;
            score: number;
            isFavorite: boolean;
            tieBreaker: string;
          } => rankedModel.score !== null,
        );

      // When searching, we only respect locked provider (by driver kind),
      // ignoring sidebar selection so account-scoped searches can find a
      // model before the user chooses a specific instance rail item.
      if (props.lockedProvider !== null) {
        const lockedProviderMatches: Array<(typeof rankedMatches)[number]> = [];
        for (const rankedModel of rankedMatches) {
          if (matchesLockedProvider(rankedModel.model)) {
            lockedProviderMatches.push(rankedModel);
          }
        }
        return lockedProviderMatches
          .toSorted((a, b) => {
            const scoreDelta = a.score - b.score;
            if (scoreDelta !== 0) {
              return scoreDelta;
            }
            if (a.isFavorite !== b.isFavorite) {
              return a.isFavorite ? -1 : 1;
            }
            return a.tieBreaker.localeCompare(b.tieBreaker);
          })
          .map((rankedModel) => rankedModel.model);
      }

      return rankedMatches
        .toSorted((a, b) => {
          const scoreDelta = a.score - b.score;
          if (scoreDelta !== 0) {
            return scoreDelta;
          }
          if (a.isFavorite !== b.isFavorite) {
            return a.isFavorite ? -1 : 1;
          }
          return a.tieBreaker.localeCompare(b.tieBreaker);
        })
        .map((rankedModel) => rankedModel.model);
    }

    // Rail no longer selects a real instance — it selects Favorites or a
    // preview COMPANY. Real configured models therefore stay reachable at the
    // top of the list: on Favorites we show favorited ones; on any company we
    // show all real configured models (company filtering only narrows the
    // preview groups below). When locked to a prior turn's driver, keep the
    // list scoped to that driver.
    if (props.lockedProvider !== null) {
      result = result.filter((m) => matchesLockedProvider(m));
      if (isFavoritesSelected) {
        result = result.filter((m) => favoritesSet.has(providerModelKey(m.instanceId, m.slug)));
      }
    } else if (isFavoritesSelected) {
      result = result.filter((m) => favoritesSet.has(providerModelKey(m.instanceId, m.slug)));
    }

    return sortProviderModelItems(result, {
      favoriteModelKeys: favoritesSet,
      groupFavorites: !isFavoritesSelected,
      instanceOrder: isFavoritesSelected ? instanceOrder : [],
    });
  }, [
    favoritesSet,
    flatModels,
    instanceOrder,
    isFavoritesSelected,
    matchesLockedProvider,
    props.lockedProvider,
    searchQuery,
  ]);

  const legacySection = useMemo(() => {
    if (isSearching || isFavoritesSelected) {
      return null;
    }
    const currentModels = filteredModels.filter((model) => !model.isLegacy);
    const legacyModels = filteredModels.filter((model) => model.isLegacy);
    if (legacyModels.length === 0) {
      return null;
    }
    return {
      key: modelPickerLegacySectionKey(props.activeInstanceId),
      currentModels,
      legacyModels,
      isExpanded: expandedLegacyInstances.has(props.activeInstanceId),
    };
  }, [
    expandedLegacyInstances,
    filteredModels,
    isFavoritesSelected,
    isSearching,
    props.activeInstanceId,
  ]);

  const visibleModels = useMemo(() => {
    if (!legacySection) {
      return filteredModels;
    }
    return [
      ...legacySection.currentModels,
      ...(legacySection.isExpanded ? legacySection.legacyModels : []),
    ];
  }, [filteredModels, legacySection]);

  const toggleLegacySection = useCallback((instanceId: ProviderInstanceId) => {
    setExpandedLegacyInstances((expanded) => {
      const next = new Set(expanded);
      if (next.has(instanceId)) {
        next.delete(instanceId);
      } else {
        next.add(instanceId);
      }
      return next;
    });
  }, []);

  const handleModelSelect = useCallback(
    (modelSlug: string, instanceId: ProviderInstanceId) => {
      // Preview/showcase rows are display-only for real coding-agent providers,
      // but for MorrowOS every catalog model IS a free model the native agent
      // can actually run — so a preview click selects the matching real model on
      // the native agent (same slug, or as a `morrow-agent:<slug>` brain).
      if (isPreviewInstanceId(instanceId)) {
        for (const [nativeInstanceId, options] of modelOptionsByInstance) {
          const nativeEntry = entryByInstanceId.get(nativeInstanceId);
          if (!nativeEntry || !isMorrowHostedDriver(nativeEntry.driverKind)) {
            continue;
          }
          // Prefer the `morrow-agent:<slug>` BRAIN form so the pick runs the
          // agent (with the user's app tools) rather than a plain hosted chat.
          const match =
            options.find((option) => option.slug === `morrow-agent:${modelSlug}`) ??
            options.find((option) => option.slug === modelSlug);
          if (match) {
            onInstanceModelChange(nativeInstanceId, match.slug);
            break;
          }
        }
        return;
      }
      if (getModelDisabledReason?.(instanceId, modelSlug)) {
        return;
      }
      const options = modelOptionsByInstance.get(instanceId);
      if (!options) {
        return;
      }
      const entry = entryByInstanceId.get(instanceId);
      if (!entry) {
        return;
      }
      // `resolveSelectableModel` uses the driver kind for normalization
      // (slug casing etc.). Custom instances share their driver's
      // normalization rules, so pass the driver kind here.
      const resolvedModel = resolveSelectableModel(entry.driverKind, modelSlug, options);
      if (resolvedModel) {
        onInstanceModelChange(instanceId, resolvedModel);
      }
    },
    [entryByInstanceId, getModelDisabledReason, modelOptionsByInstance, onInstanceModelChange],
  );

  // Slugs the native MorrowOS agent can actually run (bare + `morrow-agent:`
  // form). Catalog/preview rows for these are real, pickable free models — not
  // locked showcase decoys — so the pretty grouped view is fully selectable.
  const nativeRunnableSlugs = useMemo(() => {
    const set = new Set<string>();
    for (const [nativeInstanceId, options] of modelOptionsByInstance) {
      const nativeEntry = entryByInstanceId.get(nativeInstanceId);
      if (!nativeEntry || !isMorrowHostedDriver(nativeEntry.driverKind)) {
        continue;
      }
      for (const option of options) {
        set.add(option.slug);
        if (option.slug.startsWith("morrow-agent:")) {
          set.add(option.slug.slice("morrow-agent:".length));
        }
      }
    }
    return set;
  }, [modelOptionsByInstance, entryByInstanceId]);

  const toggleFavorite = useCallback(
    (instanceId: ProviderInstanceId, model: string) => {
      const newFavorites = [...favorites];
      const index = newFavorites.findIndex((f) => f.provider === instanceId && f.model === model);
      if (index >= 0) {
        newFavorites.splice(index, 1);
      } else {
        newFavorites.push({ provider: instanceId, model });
      }
      updateSettings({ favorites: newFavorites });
    },
    [favorites, updateSettings],
  );

  const modelJumpCommandByKey = useMemo(() => {
    const mapping = new Map<
      string,
      NonNullable<ReturnType<typeof modelPickerJumpCommandForIndex>>
    >();
    let selectableModelIndex = 0;
    for (const model of visibleModels) {
      if (getModelDisabledReason?.(model.instanceId, model.slug)) {
        continue;
      }
      const jumpCommand = modelPickerJumpCommandForIndex(selectableModelIndex);
      if (!jumpCommand) {
        return mapping;
      }
      mapping.set(modelPickerModelKey(model.instanceId, model.slug), jumpCommand);
      selectableModelIndex += 1;
    }
    return mapping;
  }, [getModelDisabledReason, visibleModels]);
  const modelJumpModelKeys = useMemo(
    () => [...modelJumpCommandByKey.keys()],
    [modelJumpCommandByKey],
  );
  // Preview (locked showcase) groups only appear in the default grouped view:
  // not while searching, not in the favorites rail, and not when the picker is
  // locked to a driver kind (editing a prior turn). A company must be selected.
  const showPreviewGroups = !isSearching && selectedCompany !== null && !catalogChromeHidden;
  // The set of catalog group instance ids for the selected company. Clicking a
  // company narrows the preview list to just that company's group(s) — both
  // its Cloudflare and Bedrock groups when it spans platforms.
  const selectedCompanyGroupIds = useMemo(
    () => new Set<string>(selectedCompany?.groupInstanceIds ?? []),
    [selectedCompany],
  );
  // Chat preview rows for the SELECTED company only (grouped, so a company that
  // exists on both platforms shows both its Cloudflare and Bedrock sections).
  const chatPreviewRowKeys = useMemo(
    (): string[] =>
      PREVIEW_GROUPS.filter((group) => selectedCompanyGroupIds.has(group.headerId)).flatMap(
        (group) => [group.headerKey, ...group.rows.map((row) => row.key)],
      ),
    [selectedCompanyGroupIds],
  );
  // Utility (background) models are hidden when a specific company is selected —
  // they're shared/platform-level, not company-scoped. Kept static for the
  // full-key list only.
  const utilityRowKeys = useMemo(
    (): string[] =>
      UTILITY_PREVIEW_GROUPS.flatMap((group) => [
        group.headerKey,
        ...group.rows.map((row) => row.key),
      ]),
    [],
  );
  const allItemKeys = useMemo(
    (): string[] => [
      ...flatModels.map((model) => modelPickerModelKey(model.instanceId, model.slug)),
      ...new Set(
        flatModels
          .filter((model) => model.isLegacy)
          .map((model) => modelPickerLegacySectionKey(model.instanceId)),
      ),
      ...chatPreviewRowKeys,
      UTILITY_TOGGLE_KEY,
      ...utilityRowKeys,
    ],
    [flatModels, chatPreviewRowKeys, utilityRowKeys],
  );
  const filteredItemKeys = useMemo((): string[] => {
    // MorrowOS: when a COMPANY is selected in the rail, show ONLY that company's
    // catalog models — not the real T3 coding-driver models (which otherwise
    // clutter the top and duplicate). Favorites / search still show real models.
    if (showPreviewGroups) {
      return [...chatPreviewRowKeys];
    }
    const modelKeys = visibleModels.map((model) =>
      modelPickerModelKey(model.instanceId, model.slug),
    );
    if (legacySection) {
      modelKeys.splice(legacySection.currentModels.length, 0, legacySection.key);
    }
    // Safety net: never render "No models found" when a catalog exists. A stale
    // locked-provider filter can drop every model; fall back to the preview
    // catalog so the picker is always usable.
    return modelKeys.length > 0 ? modelKeys : [...chatPreviewRowKeys];
  }, [chatPreviewRowKeys, legacySection, showPreviewGroups, visibleModels]);
  const filteredModelByKey = useMemo(
    (): ReadonlyMap<string, ModelPickerItem> =>
      new Map(
        visibleModels.map(
          (model) => [modelPickerModelKey(model.instanceId, model.slug), model] as const,
        ),
      ),
    [visibleModels],
  );
  const updateModelListScrollFades = useCallback(() => {
    const scrollElement = modelListRef.current?.getScrollableNode();
    if (!(scrollElement instanceof HTMLElement)) {
      return;
    }
    const maxScrollOffset = Math.max(0, scrollElement.scrollHeight - scrollElement.clientHeight);
    setShowTopScrollFade(scrollElement.scrollTop > 1);
    setShowBottomScrollFade(maxScrollOffset - scrollElement.scrollTop > 1);
  }, []);
  const modelJumpShortcutContext = useMemo(
    () =>
      ({
        terminalFocus: false,
        terminalOpen: props.terminalOpen,
        modelPickerOpen: true,
      }) as const,
    [props.terminalOpen],
  );
  const modelJumpLabelByKey = useMemo((): ReadonlyMap<string, string> => {
    if (modelJumpCommandByKey.size === 0) {
      return EMPTY_MODEL_JUMP_LABELS;
    }
    const shortcutLabelOptions = {
      platform: navigator.platform,
      context: modelJumpShortcutContext,
    };
    const mapping = new Map<string, string>();
    for (const [modelKey, command] of modelJumpCommandByKey) {
      const label = shortcutLabelForCommand(keybindings, command, shortcutLabelOptions);
      if (label) {
        mapping.set(modelKey, label);
      }
    }
    return mapping.size > 0 ? mapping : EMPTY_MODEL_JUMP_LABELS;
  }, [keybindings, modelJumpCommandByKey, modelJumpShortcutContext]);
  const modelListExtraData = useMemo(
    () => ({ favoritesSet, modelJumpLabelByKey }),
    [favoritesSet, modelJumpLabelByKey],
  );

  useEffect(() => {
    const onWindowKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) {
        return;
      }

      const command = resolveShortcutCommand(event, keybindings, {
        platform: navigator.platform,
        context: modelJumpShortcutContext,
      });
      const jumpIndex = modelPickerJumpIndexFromCommand(command ?? "");
      if (jumpIndex === null) {
        return;
      }

      const targetModelKey = modelJumpModelKeys[jumpIndex];
      if (!targetModelKey) {
        return;
      }
      const model = parseModelPickerModelKey(targetModelKey);
      if (!model) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      handleModelSelect(model.slug, model.instanceId);
    };

    window.addEventListener("keydown", onWindowKeyDown, true);

    return () => {
      window.removeEventListener("keydown", onWindowKeyDown, true);
    };
  }, [handleModelSelect, keybindings, modelJumpModelKeys, modelJumpShortcutContext]);

  useLayoutEffect(() => {
    setShowTopScrollFade(false);
    setShowBottomScrollFade(filteredItemKeys.length > 5);
    let nestedFrame = 0;
    const frame = window.requestAnimationFrame(() => {
      updateModelListScrollFades();
      nestedFrame = window.requestAnimationFrame(updateModelListScrollFades);
    });
    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(nestedFrame);
    };
  }, [filteredItemKeys, updateModelListScrollFades]);

  return (
    <TooltipProvider delay={0}>
      <div
        className="dropdown-glass relative flex h-screen max-h-86.5 w-screen max-w-90 flex-row overflow-hidden rounded-lg text-popover-foreground shadow-[0_16px_40px_-18px_rgb(0_0_0/55%)] [clip-path:inset(0_round_var(--radius-lg))] dark:shadow-[0_18px_44px_-18px_rgb(0_0_0/80%)]"
        data-model-picker-content="true"
      >
        {/* Sidebar — Favorites + one brand icon per provider company. */}
        {showSidebar && (
          <ModelPickerSidebar
            selectedId={selectedRailId}
            onSelect={handleSelectRail}
            companies={railCompanies}
            showFavorites
          />
        )}

        {/* Main content area */}
        <Combobox
          inline
          items={allItemKeys}
          filteredItems={filteredItemKeys}
          filter={null}
          autoHighlight
          open
          virtualized
          value={modelPickerModelKey(props.activeInstanceId, props.model)}
          onItemHighlighted={(modelKey, eventDetails) => {
            highlightedModelKeyRef.current = typeof modelKey === "string" ? modelKey : null;
            if (eventDetails.reason === "keyboard" && eventDetails.index >= 0) {
              void modelListRef.current?.scrollIndexIntoView?.({
                index: eventDetails.index,
                animated: false,
              });
            }
          }}
          onValueChange={(modelKey) => {
            if (typeof modelKey !== "string") {
              return;
            }
            const legacyInstanceId = parseModelPickerLegacySectionKey(modelKey);
            if (legacyInstanceId) {
              toggleLegacySection(legacyInstanceId);
              return;
            }
            const model = parseModelPickerModelKey(modelKey);
            if (model) {
              handleModelSelect(model.slug, model.instanceId);
            }
          }}
        >
          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col overflow-hidden bg-muted/40",
              showSidebar && "border-l border-border/70",
            )}
          >
            {/* Freemium upgrade banner */}
            <div className="flex items-center gap-2 px-2 pt-2 pb-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-bold leading-tight text-foreground">
                  Unlock all models
                </div>
                <div className="truncate text-[11px] leading-tight text-muted-foreground">
                  Bring your keys or upgrade to use every provider
                </div>
              </div>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleUpgrade();
                }}
                onMouseDown={(event) => event.stopPropagation()}
                className="shrink-0 rounded-md border border-border bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground transition-[filter] hover:brightness-110"
              >
                Upgrade
              </button>
            </div>
            <div className="h-0.5 shrink-0 bg-border" />

            {/* Search bar */}
            <div className="px-2 pt-2">
              <div className="border-b border-border/70 pb-2.5 transition-colors focus-within:border-ring">
                <ComboboxInput
                  ref={searchInputRef}
                  className="[&_input]:h-6.5 [&_input]:font-sans [&_input]:leading-6.5"
                  inputClassName="rounded-none bg-transparent text-sm"
                  placeholder="Search models..."
                  showTrigger={false}
                  startAddon={
                    <SearchIcon className="-translate-x-0.5 size-4 shrink-0 text-muted-foreground opacity-70" />
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      e.stopPropagation();
                      props.onRequestClose?.();
                      return;
                    }
                    if (e.key === "Enter" && highlightedModelKeyRef.current) {
                      (
                        e as typeof e & { preventBaseUIHandler?: () => void }
                      ).preventBaseUIHandler?.();
                      e.preventDefault();
                      e.stopPropagation();
                      const legacyInstanceId = parseModelPickerLegacySectionKey(
                        highlightedModelKeyRef.current,
                      );
                      if (legacyInstanceId) {
                        toggleLegacySection(legacyInstanceId);
                        return;
                      }
                      const model = parseModelPickerModelKey(highlightedModelKeyRef.current);
                      if (model) {
                        handleModelSelect(model.slug, model.instanceId);
                      }
                      return;
                    }
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  size="sm"
                  unstyled
                />
              </div>
            </div>

            {/* Model list */}
            <div className="relative min-h-0 flex-1 overflow-hidden pr-px">
              <ComboboxListVirtualized className="size-full min-w-0 p-0 not-empty:p-0">
                <LegendList<string>
                  ref={modelListRef}
                  data={filteredItemKeys}
                  extraData={modelListExtraData}
                  keyExtractor={(modelKey) => modelKey}
                  renderItem={({ item: modelKey, index }) => {
                    if (legacySection?.key === modelKey) {
                      return (
                        <ComboboxItem
                          hideIndicator
                          index={index}
                          value={modelKey}
                          aria-expanded={legacySection.isExpanded}
                          className="group w-full cursor-pointer rounded-md px-2 py-2"
                          contentClassName="flex w-full items-center gap-3"
                        >
                          <div className="min-w-0 flex-1 text-left">
                            <div className="text-xs font-medium leading-snug">Legacy models</div>
                            <div className="mt-1 text-xs font-normal leading-snug text-muted-foreground/70">
                              {legacySection.legacyModels.length} models
                            </div>
                          </div>
                          <ChevronRightIcon
                            className={cn(
                              "size-4 transition-transform",
                              legacySection.isExpanded && "rotate-90",
                            )}
                          />
                        </ComboboxItem>
                      );
                    }
                    if (modelKey === UTILITY_TOGGLE_KEY) {
                      return (
                        <ComboboxItem
                          hideIndicator
                          index={index}
                          value={modelKey}
                          aria-expanded={utilitySectionExpanded}
                          className="group mt-1 w-full cursor-pointer rounded-md px-2 py-2 opacity-80 data-disabled:pointer-events-auto"
                          contentClassName="flex w-full items-center gap-3"
                          disabled
                          onClick={(event) => {
                            event.stopPropagation();
                            setUtilitySectionExpanded((v) => !v);
                          }}
                        >
                          <div className="min-w-0 flex-1 text-left">
                            <div className="text-[11px] font-bold uppercase tracking-wide leading-snug text-muted-foreground">
                              Utility · runs in background
                            </div>
                            <div className="mt-1 text-xs font-normal leading-snug text-muted-foreground/60">
                              {UTILITY_MODEL_COUNT} speech, image &amp; embedding models · auto
                            </div>
                          </div>
                          <ChevronRightIcon
                            className={cn(
                              "size-4 shrink-0 text-muted-foreground transition-transform",
                              utilitySectionExpanded && "rotate-90",
                            )}
                          />
                        </ComboboxItem>
                      );
                    }
                    const sectionHeaderId = parseSectionHeaderKey(modelKey);
                    if (sectionHeaderId) {
                      const group = PREVIEW_GROUP_BY_HEADER_ID.get(sectionHeaderId);
                      if (!group) {
                        return null;
                      }
                      return (
                        <ProviderSectionHeader
                          displayName={group.displayName}
                          icon={group.icon}
                          {...(group.isUtility ? {} : { platform: group.platform })}
                        />
                      );
                    }
                    // Preview showcase row. For MorrowOS these are real free
                    // models the native agent can run, so render them selectable
                    // (a combobox item → handleModelSelect resolves the pick to
                    // the native agent). Only genuinely unavailable models stay
                    // locked with the upgrade flow.
                    const previewRow = PREVIEW_ROW_BY_KEY.get(modelKey);
                    if (previewRow) {
                      const runnable = nativeRunnableSlugs.has(previewRow.model.slug);
                      // Tier gate: a catalog model above the subject's entitlement
                      // is locked behind the upgrade modal (never while loading —
                      // canUseTier collapses to free, so wait for entitlements).
                      const tier = previewRow.presentation.tier;
                      const tierLocked =
                        plan.isLoaded &&
                        !previewRow.isUtility &&
                        tier !== undefined &&
                        tier !== "free" &&
                        !plan.canUseTier(tier);
                      const previewSelected =
                        props.model === previewRow.model.slug ||
                        props.model === `morrow-agent:${previewRow.model.slug}`;
                      return (
                        <ModelListRow
                          key={modelKey}
                          index={index}
                          model={previewRow.model}
                          instanceId={previewRow.instanceId}
                          driverKind={previewRow.driverKind}
                          providerDisplayName={previewRow.displayName}
                          providerIcon={previewRow.icon}
                          presentation={previewRow.presentation}
                          isLocked={!runnable || tierLocked}
                          isFavorite={false}
                          isSelected={previewSelected}
                          showProvider={false}
                          preferShortName
                          useTriggerLabel={false}
                          onUpgrade={
                            tierLocked && tier ? () => handleTierUpgrade(tier) : handleUpgrade
                          }
                          onToggleFavorite={() => {}}
                        />
                      );
                    }
                    const model = filteredModelByKey.get(modelKey);
                    if (!model) {
                      return null;
                    }
                    const disabledReason =
                      getModelDisabledReason?.(model.instanceId, model.slug) ?? null;
                    return (
                      <ModelListRow
                        key={modelKey}
                        index={index}
                        model={model}
                        instanceId={model.instanceId}
                        driverKind={model.driverKind}
                        providerDisplayName={model.instanceDisplayName}
                        providerAccentColor={model.instanceAccentColor}
                        isFavorite={favoritesSet.has(
                          providerModelKey(model.instanceId, model.slug),
                        )}
                        isSelected={
                          modelKey === modelPickerModelKey(props.activeInstanceId, props.model)
                        }
                        showProvider
                        preferShortName={!isLocked}
                        useTriggerLabel={false}
                        showNewBadge={isModelPickerNewModel(model.driverKind, model.slug)}
                        jumpLabel={modelJumpLabelByKey.get(modelKey) ?? null}
                        disabledReason={disabledReason}
                        onToggleFavorite={() => toggleFavorite(model.instanceId, model.slug)}
                      />
                    );
                  }}
                  estimatedItemSize={52}
                  drawDistance={480}
                  recycleItems
                  contentContainerClassName="pl-2 pr-px"
                  ItemSeparatorComponent={ModelListSeparator}
                  onLayout={updateModelListScrollFades}
                  onScroll={updateModelListScrollFades}
                  className={cn(
                    "scrollbar-gutter-stable h-full overflow-x-hidden overscroll-y-contain py-1.5 [&::-webkit-scrollbar-track]:my-2",
                    getVirtualizedScrollFadeClassName({
                      top: showTopScrollFade,
                      bottom: showBottomScrollFade,
                    }),
                  )}
                />
              </ComboboxListVirtualized>
            </div>
            <ComboboxEmpty className="not-empty:py-6 empty:h-0 text-xs font-normal leading-snug">
              No models found
            </ComboboxEmpty>
          </div>
        </Combobox>
      </div>
    </TooltipProvider>
  );
});
